import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast.ts';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useServiceWithAuth } from '@/hooks/useServiceWithAuth';
import { CareSpecialistService, CreateCareSpecialistPayload } from '@/services/features/CareSpecialistService.ts';
import { DropdownDataService, Clinic as ServiceClinic } from '@/services/features/DropdownDataService.ts';
import { cleanFormData, getErrorMessage, ErrorMessages } from '@/lib/utils.ts';

interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
  has_active_enroller: boolean;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  selectedClinicIds: number[];
  deferredTask: boolean;
  followUpTask: boolean;
}

export const useCareSpecialistDialog = (open: boolean, careSpecialist?: any, isEditMode: boolean = false) => {
  const { user, token, hasuraToken, logout } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    selectedClinicIds: [],
    deferredTask: false,
    followUpTask: false
  });

  // Use proper service authentication pattern
  const careSpecialistService = useServiceWithAuth(CareSpecialistService);
  const dropdownService = useServiceWithAuth(DropdownDataService);

  // Fetch clinics when dialog opens
  useEffect(() => {
    if (open && hasuraToken) {
      fetchClinics();
    }
  }, [open, hasuraToken]);

  // Populate form data after clinics are loaded (for edit mode)
  useEffect(() => {
    if (open && clinics.length > 0 && isEditMode && careSpecialist) {
      populateFormData(careSpecialist);
    } else if (open && !isEditMode) {
      resetForm();
    }
  }, [open, clinics, isEditMode, careSpecialist]);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      selectedClinicIds: [],
      deferredTask: false,
      followUpTask: false
    });
    setErrors({});
  };

  const populateFormData = (specialist: any) => {
    setFormData({
      firstName: specialist.first_name || '',
      lastName: specialist.last_name || '',
      email: specialist.email || '',
      password: '',
      confirmPassword: '',
      selectedClinicIds: specialist.clinic_ids || [],
      deferredTask: specialist.deferred_task === 'true' || specialist.deferred_task === true,
      followUpTask: specialist.follow_up === 'true' || specialist.follow_up === true
    });
    setErrors({});
  };

  const fetchClinics = async () => {
    if (!hasuraToken) {
      toast({
        title: "Error",
        description: "Authentication token not available. Please sign in again.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Fetching clinics

      const result = await dropdownService.fetchAvailableClinics();
      // Clinics API response received

      if (result.success && result.data?.pep_vw_available_clinics) {
        // Convert ServiceClinic to local Clinic interface
        const clinicsData = result.data.pep_vw_available_clinics.map((clinic: ServiceClinic) => ({
          ...clinic,
          has_active_enroller: false // Default value since service doesn't provide this
        }));
        setClinics(clinicsData);
      } else if (result.errors) {
        // GraphQL errors occurred
        toast({
          title: "Error",
          description: "Failed to load clinics - authentication error",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Error fetching clinics
      toast({
        title: "Error",
        description: "Failed to load clinics. Please try again.",
        variant: "destructive",
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = ErrorMessages.REQUIRED_FIELD;
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = ErrorMessages.REQUIRED_FIELD;
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = ErrorMessages.REQUIRED_FIELD;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = ErrorMessages.INVALID_EMAIL;
      }
    }

    // Password validation - required for create, optional for edit
    if (!isEditMode) {
      if (!formData.password.trim()) {
        newErrors.password = ErrorMessages.REQUIRED_FIELD;
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      // Confirm password validation for create mode
      if (!formData.confirmPassword.trim()) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else {
      // For edit mode, only validate if password is provided
      if (formData.password.trim() && formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (formData.password.trim() && formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Clinic selection validation
    if (formData.selectedClinicIds.length === 0) {
      newErrors.clinics = 'Please select at least one clinic';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!validateForm()) {
      return false;
    }

    // Check authentication with better error handling
    if (!user || !token) {
      // Debug log removed

      toast({
        title: "Authentication Error",
        description: ErrorMessages.SESSION_EXPIRED,
        variant: "destructive",
      });
      logout();
      return false;
    }

    setIsLoading(true);

    try {
      // Clean form data before sending
      const cleanedData = cleanFormData(formData);

      if (isEditMode && careSpecialist) {
        // Update existing Care Specialist
        const updatePayload: any = {
          first_name: cleanedData.firstName,
          last_name: cleanedData.lastName,
          email: cleanedData.email,
          clinic_ids: formData.selectedClinicIds.filter(id => id !== -1),
          deferred_task: formData.deferredTask ? "true" : "false",
          follow_up_task: formData.followUpTask ? "true" : "false"
        };

        // Only include password if provided
        if (formData.password.trim()) {
          updatePayload.password = cleanedData.password;
        }

        // Debug log removed

        const result = await careSpecialistService.updateCareSpecialist(careSpecialist.care_specialist_id, updatePayload);

        if (result.success) {
          toast({
            title: "Success",
            description: "Care Specialist updated successfully!",
          });

          return true;
        } else {
          throw new Error(result.message || 'Failed to update care specialist');
        }
      } else {
        // Create new Care Specialist
        const payload: CreateCareSpecialistPayload = {
          first_name: cleanedData.firstName,
          last_name: cleanedData.lastName,
          email: cleanedData.email,
          password: cleanedData.password,
          clinic_ids: formData.selectedClinicIds.filter(id => id !== -1),
          deferred_task: formData.deferredTask ? "true" : "false",
          follow_up_task: formData.followUpTask ? "true" : "false"
        };

        // Debug log removed

        const result = await careSpecialistService.createCareSpecialist(payload);

        if (result.success) {
          toast({
            title: "Success",
            description: "Care Specialist created successfully!",
          });

          return true;
        } else {
          throw new Error(result.message || 'Failed to create care specialist');
        }
      }
    } catch (error) {
      // Debug log removed

      if (error instanceof Error && error.message.includes('Session expired')) {
        toast({
          title: "Session Expired",
          description: ErrorMessages.SESSION_EXPIRED,
          variant: "destructive",
        });
        logout();
      } else {
        toast({
          title: "Error",
          description: getErrorMessage(error),
          variant: "destructive",
        });
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleClinicToggle = (clinicId: number) => {
    setFormData(prev => ({
      ...prev,
      selectedClinicIds: prev.selectedClinicIds.includes(clinicId)
        ? prev.selectedClinicIds.filter(id => id !== clinicId)
        : [...prev.selectedClinicIds, clinicId]
    }));
    // Clear clinic error when user selects a clinic
    if (errors.clinics) {
      setErrors(prev => ({ ...prev, clinics: '' }));
    }
  };

  const removeSelectedClinic = (clinicId: number) => {
    setFormData(prev => ({
      ...prev,
      selectedClinicIds: prev.selectedClinicIds.filter(id => id !== clinicId)
    }));
  };

  const clearFieldError = (fieldName: string) => {
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  return {
    // State
    formData,
    setFormData,
    errors,
    isLoading,
    clinics,

    // Actions
    handleSubmit,
    handleClinicToggle,
    removeSelectedClinic,
    resetForm,
    clearFieldError
  };
};